import * as _ from "lodash";
import React, { useState, useEffect, useMemo } from "react";
import * as Models from "../../../Models";
import * as Modal from "react-modal";
import DataGrid from "../DataGrid";
import { getItemGroupData } from "../../../Helpers/table";
import {
    TYPE_SITE,
    TYPE_PLANT,
    TYPE_PLANTUNIT,
    TYPE_MODULE,
    TYPE_EQUIPMENT,
    TYPE_COMPONENT,
    CHEVRON_DOWN,
} from "../../../Constants";
import { OutotecIcons } from "../Icons";
import SelectColumns, { ISelectColumn } from "./SelectColumns";
import {
    getSiteAssetsColumnState,
    setSiteAssetsColumnState,
} from "../../../Helpers/localStorage";
// import { unLinkAssetFromProject } from "../../../Middleware/assetChangeRequest";
import filterItemsStore from "../../../Store/filterItems";
import { unlinkAssetMutation } from "../../../Api/azure";
import { toast } from "react-toastify";

export const withRemover: (
    content: Models.DataGridContent,
    unLinkFunction: (assetId: string) => void
) => Models.DataGridContent = (content, unLinkFunction) => {
    const remover: any = {
        Header: "",
        id: "unlink",
        width: 80,
        maxWidth: 80,
        Cell: ({ row }) => {
            const assetId = row.original.asset_id.url.description;

            if (content.data.length > 1 && row.index === 0) {
                return <div></div>; // Skip rendering the button in the first column
            } else
                return (
                    <div>
                        <button
                            className="btn btn-primary"
                            onClick={(e) => unLinkFunction(assetId)}
                        >
                            unlink
                        </button>
                    </div>
                );
        },
    };

    return { ...content, columns: content.columns.concat(remover) };
};

function getVisibleHeaders(
    headers: string[],
    activeDetail: Models.ActiveDetail
): string[] {
    var visibleHeaders: string[] = [...headers];

    if (activeDetail.detail.type === TYPE_SITE) {
        // remove nothing
    } else if (activeDetail.detail.type === TYPE_PLANT) {
        _.remove(visibleHeaders, (h: string) => {
            return h === TYPE_PLANT;
        });
    } else if (activeDetail.detail.type === TYPE_PLANTUNIT) {
        _.remove(visibleHeaders, (h: string) => {
            return h === TYPE_PLANT || h === TYPE_PLANTUNIT;
        });
    } else if (activeDetail.detail.type === TYPE_MODULE) {
        _.remove(visibleHeaders, (h: string) => {
            return (
                h === TYPE_PLANT || h === TYPE_PLANTUNIT || h === TYPE_MODULE
            );
        });
    } else if (activeDetail.detail.type === TYPE_EQUIPMENT) {
        _.remove(visibleHeaders, (h: string) => {
            return (
                h === TYPE_PLANT ||
                h === TYPE_PLANTUNIT ||
                h === TYPE_MODULE ||
                h === TYPE_EQUIPMENT
            );
        });
    } else if (activeDetail.detail.type === TYPE_COMPONENT) {
        // Sub Equipment
        _.remove(visibleHeaders, (h: string) => {
            return (
                h === TYPE_PLANT ||
                h === TYPE_PLANTUNIT ||
                h === TYPE_MODULE ||
                h === TYPE_EQUIPMENT ||
                h.toLocaleLowerCase() === "sub equipment"
            );
        });
    }

    return visibleHeaders;
}

const sortByDescription: Function = (
    a: Models.AttributeCell,
    b: Models.AttributeCell,
    desc: boolean
) => {
    const aValue: string =
        a.url && a.url.description ? a.url.description : a.description;
    const bValue: string =
        b.url && b.url.description ? b.url.description : b.description;

    if ((a as any).isSummaryCell) {
        return !desc ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
    }
    if ((b as any).isSummaryCell) {
        return !desc ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
    }

    return aValue.localeCompare(bValue);
};

const filterCaseInsensitive: Function = (filter: any, row: any) => {
    const id: string = filter.pivotId || filter.id;
    const rowValue: any = row[id];
    if (rowValue !== null) {
        const filterValue: string =
            rowValue.url && rowValue.url.description
                ? rowValue.url.description
                : rowValue.description;
        return filterValue !== undefined
            ? String(filterValue.toString().toLowerCase()).includes(
                  filter.value.toLowerCase()
              ) || rowValue.isSummaryCell === true
            : true;
    }
};

const insertSummaryRow: Function = (gridDataContent: Models.AttributeRow[]) => {
    if (gridDataContent.length > 1) {
        const summaryRow: Models.AttributeRow = _.cloneDeep(gridDataContent[0]);

        summaryRow.items.forEach(
            (value: Models.AttributeCell, index: number) => {
                const uniqueValues: any[] = _.uniq(
                    gridDataContent.map((d) => d.items[index].url.id)
                ).filter((v) => !!v);
                summaryRow.items[index].description =
                    uniqueValues.length !== 0
                        ? uniqueValues.length.toString()
                        : "";
                summaryRow.items[index].url = {} as Models.Link;
                summaryRow.items[index].isSummaryCell = true;
                summaryRow.statusType =
                    Models.AttributeGroupItemRowStatusType.Active;
                summaryRow.isSummaryRow = true;
            }
        );
        gridDataContent.splice(0, 0, summaryRow);
    }
};

const hiddenDefaultColumns = [
    "Plant",
    "Plant Unit",
    "Module",
    "Equipment",
    "Sub Equipment",
    "Tag Number",
    "Data Status",
    "Sellable Product Model",
    "Created Date",
];

interface IProjectAssetProps {
    dataGroupName: string;
    projectAssetAttributeGroup?: Models.AttributeGroup;
    match: Models.Match;
    // activeDetail: Models.ActiveDetail;
    headers: string[];
}

export default function ProjectAssetItemGroup(props: IProjectAssetProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [assetId, setAssetId] = useState<string>("");
    const [columnOptions, setColumnOptions] = useState<ISelectColumn[]>([]);

    const { mutate: unlinkAsset, isPending: isLoading } = unlinkAssetMutation();

    const { getActiveDetail, removeProjectAssetAttributeGroups } =
        filterItemsStore();
    const activeDetail = getActiveDetail(props.match.params);

    useEffect(() => {
        const headers = props.headers ? props.headers : [];
        const visibleHeaders: string[] = getVisibleHeaders(
            headers,
            activeDetail
        );

        const columnOptions: any = visibleHeaders.map((h) => {
            return {
                value: h,
                isEnabled: h !== "Asset ID" && h !== "Asset Description",
                isSelected: !_.includes(hiddenDefaultColumns, h),
            } as ISelectColumn;
        });

        columnOptions.forEach((columnOption) => {
            var stored: ISelectColumn = getSiteAssetsColumnState(columnOption);
            columnOption.isSelected = stored.isSelected;
        });

        setColumnOptions(columnOptions);
    }, []);

    const toggleOpen = () => setIsOpen(!isOpen);
    const setClosed = () => setIsOpen(false);

    function toggleUnlinkModal(state: boolean, assetId?: string): void {
        setModalOpen(state);
        setAssetId(state ? assetId : "");
    }

    function unLinkFunction(assetId: string): void {
        toggleUnlinkModal(true, assetId);
    }

    const showHideColumn = (col: ISelectColumn) => {
        let updatedColumn: ISelectColumn;

        setColumnOptions((prevCols) => {
            const newCols = prevCols.map((c) => {
                if (c.value === col.value) {
                    updatedColumn = { ...c, isSelected: !c.isSelected };
                    return updatedColumn;
                }
                return c;
            });

            return newCols;
        });
        setSiteAssetsColumnState(updatedColumn);
    };

    function getDataGridContent(
        headers: string[],
        visibleHeaders: string[],
        rows: Models.AttributeRow[]
    ): Models.DataGridContent {
        const newRows = _.cloneDeep(rows);
        insertSummaryRow(newRows); //mutation happens for row, hence avoiding that
        const result: Models.DataGridContent = getItemGroupData(
            headers,
            newRows
        );

        result.columns.forEach((col) => {
            col.minWidth = 100;
            col.filterMethod = filterCaseInsensitive;
            col.sortMethod = sortByDescription;
            if (visibleHeaders.indexOf(col.Header) < 0) {
                col.show = false;
            }

            let selectedColumn: ISelectColumn = columnOptions.find(
                (c) => c.value === col.Header
            );
            if (selectedColumn && !selectedColumn.isSelected) {
                col.show = false;
            }
        });

        const assetDescCol: any = result.columns.find(
            (c) => c.Header === "Asset Description"
        );
        if (assetDescCol) {
            assetDescCol.fixed = "left";
            assetDescCol.width = 150;
        }

        const assetIdCol: any = result.columns.find(
            (c) => c.Header === "Asset ID"
        );
        if (assetIdCol) {
            assetIdCol.fixed = "left";
            assetIdCol.width = 125;
        }

        return result;
    }

    const handleConfirm = async () => {
        const projectId = props.match.params.parameter;
        unlinkAsset(
            { assetId, projectId },
            {
                onSuccess: () => {
                    removeProjectAssetAttributeGroups(assetId, projectId);
                    toast.success("Asset unlinked succesfully");
                },
                onError: () => {
                    toast.error("Error Unlinking Asset");
                },
            }
        );
        toggleUnlinkModal(false);
    };

    const rows: Models.AttributeRow[] =
        props.projectAssetAttributeGroup?.rows ?? [];

    const headers: string[] = props.headers ? props.headers : [];
    const name: string = props.projectAssetAttributeGroup?.name ?? "";

    const visibleHeaders: string[] = getVisibleHeaders(headers, activeDetail);

    const dataGridContent: Models.DataGridContent = getDataGridContent(
        headers,
        visibleHeaders,
        rows
    );

    return (
        <div className="ItemGroup ItemGroup--siteassets">
            <h3 className="ItemGroup__title select-columns">
                <div>
                    <span>{name}</span>
                    <span
                        className="select-columns-toggle"
                        onClick={toggleOpen}
                    >
                        Columns <OutotecIcons type={CHEVRON_DOWN} />
                    </span>
                </div>
            </h3>
            <SelectColumns
                isOpen={isOpen}
                columnOptions={columnOptions}
                closeHandler={setClosed}
                clickHandler={showHideColumn}
            />

            <DataGrid
                filterable={dataGridContent.data.length > 10}
                isSortable={true}
                showPagination={false}
                content={withRemover(dataGridContent, (assetId) =>
                    unLinkFunction(assetId)
                )}
            />
            {modalOpen && (
                <Modal
                    onRequestClose={() => toggleUnlinkModal(false)}
                    shouldCloseOnOverlayClick={true}
                    isOpen={modalOpen}
                    contentLabel="Modal"
                    styles={{}}
                    defaultStyles={{}}
                >
                    {
                        <div>
                            <h2 className="Modal__header">Unlink Asset</h2>
                            <p className="Modal__description">
                                Do you really want to unlink the asset {assetId}{" "}
                                from this project{" "}
                            </p>
                            <div className="Modal__buttonGroup">
                                <button
                                    className="btn btn-secondary"
                                    onClick={(e) => toggleUnlinkModal(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-primary float-end"
                                    onClick={(e) => handleConfirm()}
                                    disabled={isLoading}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    }
                </Modal>
            )}
        </div>
    );
}
